import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/utils';
import { Plus, Users, BookOpen } from 'lucide-react';
import { canEdit } from '@/lib/auth';
import api from '@/lib/api';

export default function ClassesManagement() {
  const [classes, setClasses] = useState([]);
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    segment: '',
    student_count: 0,
    ticket_core: 0,
    active: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [classesRes, segmentsRes] = await Promise.all([
        api.get('/classes'),
        api.get('/classes/segments')
      ]);
      setClasses(classesRes.data);
      setSegments(segmentsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar turmas');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/classes', formData);
      toast.success('Turma criada com sucesso!');
      setDialogOpen(false);
      loadData();
      setFormData({
        name: '',
        segment: '',
        student_count: 0,
        ticket_core: 0,
        active: true
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar turma');
    }
  };

  if (loading) {
    return <div className="p-6">Carregando...</div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="classes-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Gestão de Turmas</h1>
          <p className="text-muted-foreground mt-1">Gerenciar turmas e segmentos</p>
        </div>
        {canEdit() && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-class-button">
                <Plus className="w-4 h-4 mr-2" />
                Nova Turma
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nova Turma</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <Label>Nome da Turma *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </div>
                <div>
                  <Label>Segmento *</Label>
                  <Input value={formData.segment} onChange={(e) => setFormData({ ...formData, segment: e.target.value })} required />
                </div>
                <div>
                  <Label>Quantidade de Alunos</Label>
                  <Input type="number" value={formData.student_count} onChange={(e) => setFormData({ ...formData, student_count: parseInt(e.target.value) })} />
                </div>
                <div>
                  <Label>Ticket CORE (R$)</Label>
                  <Input type="number" step="0.01" value={formData.ticket_core} onChange={(e) => setFormData({ ...formData, ticket_core: parseFloat(e.target.value) })} />
                </div>
                <Button type="submit" className="w-full">Criar Turma</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Cards de Segmentos */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {segments.map((seg, idx) => (
          <Card key={idx} className="border-border/60 hover:border-primary/30 hover:shadow-md transition-all">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-primary" />
                {seg.segment}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Turmas:</span>
                  <span className="font-medium">{seg.class_count}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Alunos:</span>
                  <span className="font-medium">{seg.total_students}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Ticket Médio:</span>
                  <span className="font-medium">{formatCurrency(seg.avg_ticket)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t">
                  <span className="text-muted-foreground">Receita Potencial:</span>
                  <span className="font-bold text-secondary">{formatCurrency(seg.total_revenue)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabela de Turmas */}
      <Card className="border-border/60">
        <CardHeader>
          <CardTitle>Todas as Turmas</CardTitle>
          <CardDescription>Total: {classes.length} turmas cadastradas</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Segmento</TableHead>
                <TableHead>Alunos</TableHead>
                <TableHead>Ticket CORE</TableHead>
                <TableHead>Receita</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {classes.map((cls) => (
                <TableRow key={cls.id}>
                  <TableCell className="font-medium">{cls.name}</TableCell>
                  <TableCell>{cls.segment}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {cls.student_count}
                    </div>
                  </TableCell>
                  <TableCell>{formatCurrency(cls.ticket_core)}</TableCell>
                  <TableCell className="font-semibold">{formatCurrency(cls.student_count * cls.ticket_core)}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      cls.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {cls.active ? 'Ativa' : 'Inativa'}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
